'use client';

import React, { useEffect } from 'react';
import {
  AppstoreOutlined,
  LogoutOutlined,
  ProfileOutlined,
  SafetyOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Avatar, Dropdown, Menu, MenuProps } from 'antd';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useConnection, useUser } from '@sezenta/connection';

// CR004 super-admin shell. Distinct from NewMainLayout (the per-contract
// operational portal): this is cross-contract, with a static sider — a platform
// super-admin sees everything, so the menu is not permission-derived.
const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();
  const connection = useConnection();

  useEffect(() => {
    if (user === null) {
      navigate('/login');
    }
  }, [navigate, user]);

  const items: MenuProps['items'] = [
    {
      key: '/admin/projects',
      icon: <AppstoreOutlined />,
      label: 'Projects',
      onClick: () => navigate('/admin/projects'),
    },
    {
      key: '/admin/users',
      icon: <TeamOutlined />,
      label: 'Users (global)',
      onClick: () => navigate('/admin/users'),
    },
    {
      key: '/admin/roles',
      icon: <SafetyOutlined />,
      label: 'Roles & Permissions',
      onClick: () => navigate('/admin/roles'),
    },
    {
      key: '/admin/master-data',
      icon: <ProfileOutlined />,
      label: 'Cross-Project Master Data',
      onClick: () => navigate('/admin/master-data'),
    },
  ];

  const userMenu = (
    <Menu>
      <Menu.Item
        key="logout"
        icon={<LogoutOutlined />}
        onClick={() => {
          connection.logout();
          navigate('/login');
        }}
      >
        Logout
      </Menu.Item>
    </Menu>
  );

  const selectedKey =
    items
      .map((i) => i?.key as string)
      .find((k) => location.pathname.startsWith(k)) ?? '/admin/projects';

  return (
    <div className="w-full h-screen flex main-layout">
      <div className="w-full h-full flex">
        <div className="h-full w-fit bg-white side-nav flex flex-col">
          <div
            className="flex items-center px-4 h-[60px] min-h-[60px] w-full bg-white gap-2 mb-2"
            style={{ boxShadow: '0px 1px 0px rgba(18, 32, 59, 0.09)' }}
          >
            <img src="/img/logo.png" alt="logo" className="w-[88px] h-[38px]" />
          </div>
          <div className="w-full flex justify-start items-center py-2 gap-2 px-4">
            <div className="flex flex-col gap-1 w-full items-start">
              <div className="text-[#252C32] text-[14px] font-semibold">
                Administration
              </div>
              <span className="inline-flex items-center bg-[#E6F4F4] text-[#1A8B8F] text-[11px] font-semibold px-2 py-0.5 rounded-full">
                Super Admin
              </span>
            </div>
          </div>
          <Menu
            className="h-full w-full bg-white"
            mode="inline"
            items={items}
            selectedKeys={[selectedKey]}
            style={{ minWidth: 240 }}
          />
        </div>

        <div className="h-full w-screen flex flex-col overflow-y-scroll">
          <div
            className="w-full h-[60px] min-h-[60px] bg-white items-center flex px-4"
            style={{ boxShadow: '0px 1px 0px rgba(18, 32, 59, 0.09)' }}
          >
            <div className="flex items-center ml-auto pr-2 gap-5">
              <Dropdown overlay={userMenu} trigger={['click']}>
                <div className="gap-2 flex items-center cursor-pointer">
                  <Avatar icon={<UserOutlined />} src={user?.picture}>
                    U
                  </Avatar>
                  <p>
                    {user?.firstName} {user?.lastName}
                  </p>
                </div>
              </Dropdown>
            </div>
          </div>
          <div className="w-full h-[calc(100%-60px)] px-4 mb-4">
            <div
              className="bg-white w-full h-full flex"
              style={{
                border: '1px solid #EAECF0',
                boxShadow: '0px 4px 8px -2px rgba(16, 24, 40, 0.1)',
                borderRadius: '8px',
              }}
            >
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
